import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, resolveImageUrl } from "../lib/config";
import { Megaphone, ChevronLeft, ChevronRight } from "lucide-react";

const AUTOPLAY_MS = 6000;

export default function Pubblicita() {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [direction, setDirection] = useState(1);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/pubblicita")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  const go = useCallback((dir) => {
    if (items.length === 0) return;
    setDirection(dir);
    setIndex((i) => (i + dir + items.length) % items.length);
  }, [items.length]);

  // Autoplay
  useEffect(() => {
    if (items.length < 2 || paused) return;
    timerRef.current = setInterval(() => {
      setDirection(1);
      setIndex((i) => (i + 1) % items.length);
    }, AUTOPLAY_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [items.length, paused]);

  if (items.length === 0) return null;

  const current = items[index];

  return (
    <section
      id="pubblicita"
      className="relative py-16 md:py-20 px-5 md:px-8"
      data-testid="pubblicita-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.4em] uppercase text-fp-cyan font-heading font-semibold">
            <Megaphone size={14} /> In evidenza
          </div>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tight">
            <span className="silver-gradient">Novità &</span>{" "}
            <span className="cyan-gradient">Pubblicità</span>
          </h2>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div
            className="relative rounded-3xl overflow-hidden border border-white/10 bg-fp-surface/40 backdrop-blur-sm"
            style={{ minHeight: "clamp(280px, 55vw, 640px)" }}
            data-testid="pubblicita-carousel"
          >
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={current.id || index}
                custom={direction}
                initial={{ opacity: 0, x: direction * 80 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -direction * 80 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.25}
                onDragEnd={(_, info) => {
                  if (info.offset.x < -60) go(1);
                  else if (info.offset.x > 60) go(-1);
                }}
                className="absolute inset-0 flex items-center justify-center cursor-grab active:cursor-grabbing"
                data-testid={`pubblicita-slide-${index}`}
              >
                <img
                  src={resolveImageUrl(current.image_url)}
                  alt={current.title || `Pubblicità ${index + 1}`}
                  className="w-full h-full object-contain bg-fp-bg select-none pointer-events-none"
                  draggable={false}
                  loading="eager"
                />
                {(current.title || current.subtitle) && (
                  <div className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-fp-bg/95 via-fp-bg/60 to-transparent px-6 md:px-10 py-6 md:py-8 pointer-events-none">
                    {current.title && (
                      <div className="font-heading font-black text-xl md:text-3xl text-white drop-shadow" data-testid="pubblicita-title">
                        {current.title}
                      </div>
                    )}
                    {current.subtitle && (
                      <div className="mt-1 text-white/80 text-sm md:text-base max-w-3xl">
                        {current.subtitle}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {items.length > 1 && (
              <>
                <button
                  onClick={() => go(-1)}
                  aria-label="Precedente"
                  className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full bg-fp-bg/80 border border-white/10 text-white hover:text-fp-cyan hover:border-fp-cyan/50 backdrop-blur transition-all flex items-center justify-center"
                  data-testid="pubblicita-prev"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  onClick={() => go(1)}
                  aria-label="Successiva"
                  className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full bg-fp-bg/80 border border-white/10 text-white hover:text-fp-cyan hover:border-fp-cyan/50 backdrop-blur transition-all flex items-center justify-center"
                  data-testid="pubblicita-next"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
          </div>

          {/* Dots */}
          {items.length > 1 && (
            <div className="flex justify-center gap-2 mt-5" data-testid="pubblicita-dots">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }}
                  aria-label={`Vai alla slide ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${
                    i === index ? "w-8 bg-fp-cyan" : "w-2 bg-white/25 hover:bg-white/50"
                  }`}
                  data-testid={`pubblicita-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
